import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { User, UserCounts, PaginationInfo } from '../services/userManagementService';

// State interface
interface UserManagementState {
  users: User[];
  userCounts: UserCounts | null;
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  selectedUsers: string[];
  searchQuery: string;
  searchFilters: {
    role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN';
    disabled?: boolean;
  };
  editingUser: User | null;
  showEditSidebar: boolean;
  showDeleteModal: boolean;
  showDisableModal: boolean;
  userToDelete: User | null;
  userToDisable: User | null;
}

// Action types
type UserManagementAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USERS'; payload: { users: User[]; pagination: PaginationInfo } }
  | { type: 'SET_USER_COUNTS'; payload: UserCounts }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'REMOVE_USER'; payload: string }
  | { type: 'SET_SELECTED_USERS'; payload: string[] }
  | { type: 'TOGGLE_USER_SELECTION'; payload: string }
  | { type: 'CLEAR_SELECTED_USERS' }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_SEARCH_FILTERS'; payload: { role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN'; disabled?: boolean } }
  | { type: 'SET_EDITING_USER'; payload: User | null }
  | { type: 'SET_SHOW_EDIT_SIDEBAR'; payload: boolean }
  | { type: 'SET_SHOW_DELETE_MODAL'; payload: boolean }
  | { type: 'SET_SHOW_DISABLE_MODAL'; payload: boolean }
  | { type: 'SET_USER_TO_DELETE'; payload: User | null }
  | { type: 'SET_USER_TO_DISABLE'; payload: User | null }
  | { type: 'RESET_STATE' };

// Initial state
const initialState: UserManagementState = {
  users: [],
  userCounts: null,
  pagination: null,
  loading: false,
  error: null,
  selectedUsers: [],
  searchQuery: '',
  searchFilters: {},
  editingUser: null,
  showEditSidebar: false,
  showDeleteModal: false,
  showDisableModal: false,
  userToDelete: null,
  userToDisable: null,
};

// Reducer
function userManagementReducer(state: UserManagementState, action: UserManagementAction): UserManagementState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_USERS':
      return { 
        ...state, 
        users: action.payload.users, 
        pagination: action.payload.pagination,
        loading: false,
        error: null
      };
    
    case 'SET_USER_COUNTS':
      return { ...state, userCounts: action.payload };
    
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map(user => 
          user.id === action.payload.id ? action.payload : user
        ),
        editingUser: state.editingUser?.id === action.payload.id ? action.payload : state.editingUser,
      };
    
    case 'REMOVE_USER':
      return {
        ...state,
        users: state.users.filter(user => user.id !== action.payload),
        selectedUsers: state.selectedUsers.filter(id => id !== action.payload),
      };
    
    case 'SET_SELECTED_USERS':
      return { ...state, selectedUsers: action.payload };
    
    case 'TOGGLE_USER_SELECTION':
      return {
        ...state,
        selectedUsers: state.selectedUsers.includes(action.payload)
          ? state.selectedUsers.filter(id => id !== action.payload)
          : [...state.selectedUsers, action.payload]
      };
    
    case 'CLEAR_SELECTED_USERS':
      return { ...state, selectedUsers: [] };
    
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    
    case 'SET_SEARCH_FILTERS':
      return { ...state, searchFilters: action.payload };
    
    case 'SET_EDITING_USER':
      return { ...state, editingUser: action.payload };
    
    case 'SET_SHOW_EDIT_SIDEBAR':
      return { ...state, showEditSidebar: action.payload };
    
    case 'SET_SHOW_DELETE_MODAL':
      return { ...state, showDeleteModal: action.payload };
    
    case 'SET_SHOW_DISABLE_MODAL':
      return { ...state, showDisableModal: action.payload };
    
    case 'SET_USER_TO_DELETE':
      return { ...state, userToDelete: action.payload };
    
    case 'SET_USER_TO_DISABLE':
      return { ...state, userToDisable: action.payload };
    
    case 'RESET_STATE':
      return initialState;
    
    default:
      return state;
  }
}

// Context interface
interface UserManagementContextType {
  state: UserManagementState;
  dispatch: React.Dispatch<UserManagementAction>;
  
  // Helper functions
  selectUser: (userId: string) => void;
  selectAllUsers: () => void;
  clearSelection: () => void;
  isUserSelected: (userId: string) => boolean;
  isAllSelected: () => boolean;
  
  // Modal helpers
  openEditSidebar: (user: User) => void;
  closeEditSidebar: () => void;
  openDeleteModal: (user: User) => void;
  closeDeleteModal: () => void;
  openDisableModal: (user: User) => void;
  closeDisableModal: () => void;
  
  // Search helpers
  setSearchQuery: (query: string) => void;
  setSearchFilters: (filters: { role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN'; disabled?: boolean }) => void;
  clearSearch: () => void;
}

// Create context
const UserManagementContext = createContext<UserManagementContextType | undefined>(undefined);

// Provider component
interface UserManagementProviderProps {
  children: ReactNode;
}

export function UserManagementProvider({ children }: UserManagementProviderProps) {
  const [state, dispatch] = useReducer(userManagementReducer, initialState);

  // Helper functions
  const selectUser = (userId: string) => {
    dispatch({ type: 'TOGGLE_USER_SELECTION', payload: userId });
  };

  const selectAllUsers = () => {
    const allUserIds = state.users.map(user => user.id);
    dispatch({ type: 'SET_SELECTED_USERS', payload: allUserIds });
  };

  const clearSelection = () => {
    dispatch({ type: 'CLEAR_SELECTED_USERS' });
  };

  const isUserSelected = (userId: string) => {
    return state.selectedUsers.includes(userId);
  };

  const isAllSelected = () => {
    return state.users.length > 0 && state.selectedUsers.length === state.users.length;
  };

  // Modal helpers
  const openEditSidebar = (user: User) => {
    dispatch({ type: 'SET_EDITING_USER', payload: user });
    dispatch({ type: 'SET_SHOW_EDIT_SIDEBAR', payload: true });
  };

  const closeEditSidebar = () => {
    dispatch({ type: 'SET_EDITING_USER', payload: null });
    dispatch({ type: 'SET_SHOW_EDIT_SIDEBAR', payload: false });
  };

  const openDeleteModal = (user: User) => {
    dispatch({ type: 'SET_USER_TO_DELETE', payload: user });
    dispatch({ type: 'SET_SHOW_DELETE_MODAL', payload: true });
  };

  const closeDeleteModal = () => {
    dispatch({ type: 'SET_USER_TO_DELETE', payload: null });
    dispatch({ type: 'SET_SHOW_DELETE_MODAL', payload: false });
  };

  const openDisableModal = (user: User) => {
    dispatch({ type: 'SET_USER_TO_DISABLE', payload: user });
    dispatch({ type: 'SET_SHOW_DISABLE_MODAL', payload: true });
  };

  const closeDisableModal = () => {
    dispatch({ type: 'SET_USER_TO_DISABLE', payload: null });
    dispatch({ type: 'SET_SHOW_DISABLE_MODAL', payload: false });
  };

  // Search helpers
  const setSearchQuery = (query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  };

  const setSearchFilters = (filters: { role?: 'PATIENT' | 'CAREGIVER' | 'ADMIN'; disabled?: boolean }) => {
    dispatch({ type: 'SET_SEARCH_FILTERS', payload: filters });
  };

  const clearSearch = () => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: '' });
    dispatch({ type: 'SET_SEARCH_FILTERS', payload: {} });
  };

  const contextValue: UserManagementContextType = {
    state,
    dispatch,
    selectUser,
    selectAllUsers,
    clearSelection,
    isUserSelected,
    isAllSelected,
    openEditSidebar,
    closeEditSidebar,
    openDeleteModal,
    closeDeleteModal,
    openDisableModal,
    closeDisableModal,
    setSearchQuery,
    setSearchFilters,
    clearSearch,
  };

  return (
    <UserManagementContext.Provider value={contextValue}>
      {children}
    </UserManagementContext.Provider>
  );
}

// Hook to use the context
export function useUserManagement() {
  const context = useContext(UserManagementContext);
  if (context === undefined) {
    throw new Error('useUserManagement must be used within a UserManagementProvider');
  }
  return context;
}
